import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { sessionBelongsToLots } from '../sessionScope';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddParkingLotDialog from '../components/AddParkingLotDialog';
import EditParkingLotDialog from '../components/EditParkingLotDialog';

interface ParkingLot {
  id: string;
  lotCode?: string;
  lotName: string;
  address: string;
  ratePeriod?: string;
  currencies?: Record<string, any>;
  ratePerHour?: number;
  availability: number;
  totalSpots?: number;
  hasAssignedSpaces?: boolean;
  vendorId?: string;
  timezone?: string;
  salesTaxRate?: number;
  maxOvertimeHours?: number | null;
  latlon?: any;
  pricingType?: string;
  dailyMaximum?: number;
  flatFee?: number;
  flatFeeDuration?: number;
  // Approval state. Missing field === legacy lot, treated as approved.
  status?: 'pending_review' | 'approved' | 'rejected';
  rejectionReason?: string;
}

interface VendorParkingLotsProps {
  vendorId: string;
  // False until the partner verifies their email. The dashboard banner has
  // always promised that verification gates lot submission; this is what
  // actually enforces it.
  canSubmitLots?: boolean;
}

export default function VendorParkingLots({ vendorId, canSubmitLots = true }: VendorParkingLotsProps) {
  const [lots, setLots] = useState<ParkingLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState<ParkingLot | null>(null);

  const fetchMyLots = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'parkinglots'),
        where('vendorId', '==', vendorId)
      );
      const querySnapshot = await getDocs(q);
      const lotsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ParkingLot[];
      setLots(lotsData);
    } catch (error) {
      console.error('Error fetching parking lots:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyLots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const handleDeleteLot = async (lotId: string, lotName: string) => {
    // Deleting a lot out from under running sessions strands the parkers on
    // it: the session keeps billing but the lot it points at is gone.
    try {
      const activeSnapshot = await getDocs(query(
        collection(db, 'parkingsessions'),
        where('sessionActive', '==', true)
      ));
      const activeHere = activeSnapshot.docs.filter((d) =>
        sessionBelongsToLots(d.data() as any, new Set([lotId]), new Set([lotName]))
      ).length;

      if (activeHere > 0) {
        alert(
          `"${lotName}" still has ${activeHere} active session` +
          `${activeHere === 1 ? '' : 's'}.\n\n` +
          'End or wait out those sessions before deleting the lot.'
        );
        return;
      }
    } catch (error: any) {
      alert('Could not check for active sessions, so the lot was not deleted: ' + error.message);
      return;
    }

    if (!window.confirm(`Delete "${lotName}"? This cannot be undone.`)) {
      return;
    }
    if (!window.confirm(`Final confirmation: permanently delete "${lotName}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'parkinglots', lotId));
      alert('Parking lot deleted.');
      fetchMyLots();
    } catch (error: any) {
      alert('Failed to delete: ' + error.message);
    }
  };

  const getPricingDisplay = (lot: ParkingLot) => {
    if (lot.currencies && Object.keys(lot.currencies).length > 0) {
      return Object.entries(lot.currencies)
        .map(([code, pricing]) => `${code} ${pricing.rate ?? 'N/A'}/${lot.ratePeriod || 'hour'}`)
        .join(', ');
    }
    if (lot.ratePerHour) return `$${lot.ratePerHour}/hour`;
    return 'N/A';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1,
        mb: 2
      }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          My Parking Lots
        </Typography>
        <Tooltip
          title={canSubmitLots ? '' : 'Verify your email address to submit a parking lot'}
        >
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              size="small"
              disabled={!canSubmitLots}
            >
              Add Parking Lot
            </Button>
          </span>
        </Tooltip>
      </Box>

      {lots.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <Typography color="textSecondary">
            {canSubmitLots
              ? 'No parking lots yet. Click "Add Parking Lot" to create one.'
              : 'No parking lots yet. Verify your email address to submit your first lot.'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Lot Code</TableCell>
                  <TableCell>Lot Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Pricing</TableCell>
                  <TableCell>Available</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.map((lot) => (
                  <TableRow key={lot.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {lot.lotCode || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{lot.lotName}</Typography>
                        {lot.status === 'rejected' && lot.rejectionReason && (
                          // Inline rejection feedback so the partner knows
                          // why and what to fix before resubmitting. Kept
                          // small + italic so it doesn't dominate the row.
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', fontStyle: 'italic', mt: 0.5, whiteSpace: 'pre-wrap' }}
                          >
                            Reason: {lot.rejectionReason}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{lot.address}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {getPricingDisplay(lot)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lot.availability ?? '—'} / {lot.totalSpots ?? '—'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Approval state takes priority over occupancy:
                        // a lot that isn't approved can't be booked, so
                        // showing "Open" / "Full" would be misleading.
                        if (lot.status === 'pending_review') {
                          return (
                            <Chip
                              label="Pending Approval"
                              color="warning"
                              size="small"
                            />
                          );
                        }
                        if (lot.status === 'rejected') {
                          return (
                            <Chip label="Rejected" color="error" size="small" />
                          );
                        }
                        return (
                          <Chip
                            label={(lot.availability ?? 0) > 0 ? 'Open' : 'Full'}
                            color={(lot.availability ?? 0) > 0 ? 'success' : 'error'}
                            size="small"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {lot.status === 'rejected' ? (
                        <Tooltip title="Edit the lot details and resubmit for review">
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={() => {
                              setSelectedLot(lot);
                              setEditDialogOpen(true);
                            }}
                            sx={{ mr: 0.5 }}
                          >
                            Edit & Resubmit
                          </Button>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Edit lot">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedLot(lot);
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete lot">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLot(lot.id, lot.lotName)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <AddParkingLotDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={fetchMyLots}
        vendorId={vendorId}
      />

      <EditParkingLotDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedLot(null);
        }}
        lot={selectedLot}
        onSuccess={fetchMyLots}
        vendorId={vendorId}
      />
    </Box>
  );
}
